import { Injectable, inject } from '@angular/core';
import { SuperAdminElevation } from '@core/auth/superadmin-elevation.model';
import { TokenResponseDto } from '@features/shared/auth/data/dtos/auth.dto';
import { AuthMapper } from '@features/shared/auth/data/mappers/auth.mapper';
import { ClientPlatform } from '@shared/utils/client-platform.util';
import { Observable, from, map, switchMap } from 'rxjs';
import { Passkey } from '../../domain/models/passkey.model';
import {
  IPasskeyRepository, PasskeyAuthenticationResult, PasskeyEnrollmentAuth, PasskeyEnrollmentResult,
  PasskeyStepUpProof,
} from '../../domain/repositories/passkey.repository';
import { PasskeyRemoteDatasource } from '../datasources/remote/passkey.remote-datasource';
import { PasskeyDto } from '../dtos/passkey.dto';
import { PasskeyMapper } from '../mappers/passkey.mapper';
import { WebAuthnService } from '../services/webauthn.service';

@Injectable()
export class PasskeyRepositoryImpl implements IPasskeyRepository {
  private readonly datasource = inject(PasskeyRemoteDatasource);
  private readonly webAuthn = inject(WebAuthnService);
  private readonly mapper = inject(PasskeyMapper);
  private readonly authMapper = inject(AuthMapper);

  // Every `switchMap` into `webAuthn.create`/`webAuthn.get` below must stay glued to the options
  // response that precedes it: browsers only honour `navigator.credentials` while the originating
  // user gesture is still "transiently active", so nothing (a debounce, a delay, an extra
  // confirmation round trip) may be inserted between the two.
  //
  // 🔴 The corollary is why `verifyForEnrollment` stops where it does. Adding a passkey needs two
  // ceremonies -- assert with an existing key, then create the new one -- and the first `get()`
  // spends the activation, so a single click cannot pay for both. Each method here is exactly one
  // ceremony, and the add flow is deliberately split across two clicks.
  public verifyForEnrollment(): Observable<PasskeyStepUpProof> {
    return this.datasource.getManagementOptions('add').pipe(
      switchMap((challengeDto) => {
        const challenge = this.mapper.toChallenge(challengeDto);
        return from(this.webAuthn.get(challenge.options)).pipe(
          map(credential => ({ challengeId: challenge.challengeId, credential })),
        );
      }),
    );
  }

  public enroll(
    friendlyName: string, auth: PasskeyEnrollmentAuth, platform: ClientPlatform | null,
  ): Observable<PasskeyEnrollmentResult> {
    return this.datasource.getRegistrationOptions(auth, platform).pipe(
      switchMap((challengeDto) => {
        const challenge = this.mapper.toChallenge(challengeDto);
        return from(this.webAuthn.create(challenge.options)).pipe(
          switchMap(credential => this.datasource.register(challenge.challengeId, credential, friendlyName)),
        );
      }),
      map(response => ({
        passkey: this.mapper.toPasskey(response.body as PasskeyDto),
        elevation: this.mapper.toElevationFromHeaders(response.headers),
      })),
    );
  }

  public list(): Observable<readonly Passkey[]> {
    return this.datasource.list().pipe(map(dtos => this.mapper.toPasskeys(dtos)));
  }

  public rename(id: string, friendlyName: string): Observable<void> {
    return this.datasource.rename(id, friendlyName);
  }

  // Same glued shape as `elevate()` below: options -> assertion -> the operation the assertion
  // authorizes, with the deletion itself carrying the proof.
  public remove(id: string): Observable<void> {
    return this.datasource.getManagementOptions('delete').pipe(
      switchMap((challengeDto) => {
        const challenge = this.mapper.toChallenge(challengeDto);
        return from(this.webAuthn.get(challenge.options)).pipe(
          switchMap(credential =>
            this.datasource.remove(id, { challengeId: challenge.challengeId, credential }),
          ),
        );
      }),
    );
  }

  public requestRecoveryCode(password: string): Observable<void> {
    return this.datasource.requestRecovery(password);
  }

  public confirmRecovery(password: string, code: string): Observable<number> {
    return this.datasource.confirmRecovery(password, code).pipe(map(result => result.RemovedCount));
  }

  // Reuses the auth slice's own `AuthMapper` so a passkey login produces a byte-identical
  // `AuthSession` to a password login -- the backend returns the same `TokenResponse`, and every
  // downstream consumer (token storage, refresh scheduler, session-conflict watcher) stays unaware
  // of which of the two ceremonies produced the session.
  public authenticate(username: string | null, remember: boolean): Observable<PasskeyAuthenticationResult> {
    return this.datasource.getAssertionOptions(username).pipe(
      switchMap((challengeDto) => {
        const challenge = this.mapper.toChallenge(challengeDto);
        return from(this.webAuthn.get(challenge.options)).pipe(
          switchMap(credential => this.datasource.login(challenge.challengeId, credential, remember)),
        );
      }),
      map(response => ({
        session: this.authMapper.toSession(response.body as TokenResponseDto),
        elevation: this.mapper.toElevationFromHeaders(response.headers),
      })),
    );
  }

  // Same glued shape as `authenticate` above, and for the same reason: nothing may come between
  // the options response and `navigator.credentials.get()` or the transient user activation lapses
  // and the browser refuses the prompt.
  public elevate(): Observable<SuperAdminElevation> {
    return this.datasource.getElevationOptions().pipe(
      switchMap((challengeDto) => {
        const challenge = this.mapper.toChallenge(challengeDto);
        return from(this.webAuthn.get(challenge.options)).pipe(
          switchMap(credential => this.datasource.elevate(challenge.challengeId, credential)),
        );
      }),
      map(dto => this.mapper.toElevation(dto)),
    );
  }
}
